/* ============================================================
   Quill&Steel — Portal hero interactions
   ------------------------------------------------------------
   - Portal zoom (1.8 -> 1.0) driven by requestAnimationFrame
     with a cubic-bezier(0.65,0,0.35,1) easing solver.
   - Mouse parallax (glow / scene / portals / foliage).
   - Cursor-reactive foliage with quadratic falloff + lerp.

   Ported from the standalone prototype, with storefront changes:
   * Scoped per section element, not document-wide getElementById,
     so the theme editor can re-render the section safely.
   * Pointer coords are relative to the hero's rect (the hero is a
     band on a scrolling page, not the whole viewport).
   * RAF parks itself while the hero is scrolled out of view.
   * No custom cursor, no nav.
   ============================================================ */

(function () {
  "use strict";

  var ZOOM_START = 1.8;
  var ZOOM_END = 1.0;
  var ZOOM_DUR = 2500; // ms

  var PARALLAX = { glow: 4, scene: 10, portal: 12, foliage: 20, rune: 26 };
  var FOLIAGE = { radius: 400, push: 48, lerp: 0.08, rot: 2.5 };

  /* ---------- cubic-bezier(0.65, 0, 0.35, 1) solver ---------- */
  function makeCubicBezier(x1, y1, x2, y2) {
    var cx = 3 * x1;
    var bx = 3 * (x2 - x1) - cx;
    var ax = 1 - cx - bx;
    var cy = 3 * y1;
    var by = 3 * (y2 - y1) - cy;
    var ay = 1 - cy - by;

    function sampleX(t) {
      return ((ax * t + bx) * t + cx) * t;
    }
    function sampleY(t) {
      return ((ay * t + by) * t + cy) * t;
    }
    function sampleDX(t) {
      return (3 * ax * t + 2 * bx) * t + cx;
    }

    function solveX(x) {
      var t = x;
      for (var i = 0; i < 8; i++) {
        var xt = sampleX(t) - x;
        if (Math.abs(xt) < 1e-6) return t;
        var d = sampleDX(t);
        if (Math.abs(d) < 1e-6) break;
        t -= xt / d;
      }
      var lo = 0;
      var hi = 1;
      t = x;
      while (lo < hi) {
        var sx = sampleX(t);
        if (Math.abs(sx - x) < 1e-6) return t;
        if (x > sx) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
      }
      return t;
    }

    return function (x) {
      return sampleY(solveX(x));
    };
  }

  var portalEase = makeCubicBezier(0.65, 0, 0.35, 1);

  function clamp(v, a, b) {
    return Math.max(a, Math.min(b, v));
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function initHero(root) {
    if (!root || root.dataset.hpInit === "1") return;
    root.dataset.hpInit = "1";

    var q = function (sel) {
      return root.querySelector(sel);
    };

    var scene = q("[data-hp-scene]");
    var glow = q("[data-hp-glow]");
    var portalLeft = q("[data-hp-portal-left]");
    var portalRight = q("[data-hp-portal-right]");
    var portals = [portalLeft, portalRight].filter(Boolean);

    // Each foliage layer is a full-frame canvas, so its element centre is the
    // hero centre for all four. Anchor the cursor-influence point to where the
    // layer's *content* actually sits (its corner), in hero-rect fractions.
    var foliage = [
      { el: q("[data-hp-foliage-tl]"), ax: 0.15, ay: 0.15 },
      { el: q("[data-hp-foliage-tr]"), ax: 0.85, ay: 0.15 },
      { el: q("[data-hp-foliage-bl]"), ax: 0.15, ay: 0.85 },
      { el: q("[data-hp-foliage-br]"), ax: 0.85, ay: 0.85 }
    ].filter(function (f) {
      return f.el;
    });

    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* ---------- state ---------- */
    var zoomStartTime = null;
    var zoomScale = ZOOM_START;

    var mxNorm = 0;
    var myNorm = 0;
    var mxLerp = 0;
    var myLerp = 0;

    var pointerX = 0;
    var pointerY = 0;
    var pointerActive = false;

    var rafId = null;
    var visible = true;
    var timers = [];

    var foliageState = foliage.map(function () {
      return { x: 0, y: 0, rot: 0 };
    });

    /* ---------- runes ---------- */
    // The same field the feature panels run (rune-field.js), so the motif
    // carries from the hero into the page instead of changing form at the
    // fold. Attached by hand rather than by the field's own auto-attach: the
    // hero holds it back until the intro reaches 2200ms, and this loop drives
    // its parallax so the glyphs drift with the rest of the depth layers.
    var runeLayer = q("[data-hp-runes]");

    function attachRunes() {
      if (!runeLayer || !window.QSRuneField) return;
      window.QSRuneField.attach(runeLayer, {
        count: parseInt(runeLayer.getAttribute("data-count"), 10)
      });
    }

    /* ---------- input (coords relative to the hero band) ---------- */
    function onPointerMove(e) {
      var r = root.getBoundingClientRect();
      pointerX = e.clientX - r.left;
      pointerY = e.clientY - r.top;
      pointerActive = true;

      var cx = r.width / 2;
      var cy = r.height / 2;
      if (cx > 0 && cy > 0) {
        mxNorm = clamp((pointerX - cx) / cx, -1, 1);
        myNorm = clamp((pointerY - cy) / cy, -1, 1);
      }
    }

    function onPointerLeave() {
      // Settle back to rest rather than freezing mid-push.
      pointerActive = false;
      mxNorm = 0;
      myNorm = 0;
    }

    root.addEventListener("pointermove", onPointerMove, { passive: true });
    root.addEventListener("pointerleave", onPointerLeave, { passive: true });
    window.addEventListener("blur", onPointerLeave);

    /* ---------- intro timeline (opacity via classes; zoom via RAF) ---------- */
    function after(ms, fn) {
      timers.push(setTimeout(fn, ms));
    }

    function runIntro() {
      zoomStartTime = performance.now();

      after(2200, function () {
        portals.forEach(function (p) {
          p.classList.add("is-out");
        });
        foliage.forEach(function (f) {
          f.el.classList.add("is-in");
        });
        if (runeLayer) runeLayer.classList.add("is-in");
      });
    }

    function showStaticState() {
      zoomScale = ZOOM_END;
      portals.forEach(function (p) {
        p.classList.add("is-out");
      });
      foliage.forEach(function (f) {
        f.el.classList.add("is-in");
      });
      if (runeLayer) runeLayer.classList.add("is-in");
    }

    /* ---------- RAF loop ---------- */
    function tick(now) {
      if (zoomStartTime !== null && zoomScale > ZOOM_END) {
        var p = clamp((now - zoomStartTime) / ZOOM_DUR, 0, 1);
        zoomScale = ZOOM_START + (ZOOM_END - ZOOM_START) * portalEase(p);
        if (p >= 1) zoomScale = ZOOM_END;
      }

      mxLerp = lerp(mxLerp, mxNorm, 0.08);
      myLerp = lerp(myLerp, myNorm, 0.08);

      // The base and glow zoom in lockstep with the portal halves.
      //
      // The prototype held the base static while the halves zoomed from 1.8.
      // That leaves the same forest rendered at two magnifications at once, so
      // wherever the halves are less than fully opaque - including the
      // feathered band where they meet - the two mismatched versions blend and
      // leave a hard vertical line down the middle for the whole intro.
      // Zooming every layer together keeps them registered, so the halves
      // dissolve into an identical base and the seam cannot appear.
      if (glow) {
        glow.style.transform =
          "translate(" + -mxLerp * PARALLAX.glow + "px," + -myLerp * PARALLAX.glow +
          "px) scale(" + zoomScale + ")";
      }
      if (scene) {
        scene.style.transform =
          "translate(" + -mxLerp * PARALLAX.scene + "px," + -myLerp * PARALLAX.scene +
          "px) scale(" + zoomScale + ")";
      }

      // Portal halves: parallax composed with the live zoom scale.
      //
      var pt =
        "translate(" +
        -mxLerp * PARALLAX.portal +
        "px," +
        -myLerp * PARALLAX.portal +
        "px) scale(" +
        zoomScale +
        ")";
      portals.forEach(function (el) {
        el.style.transform = pt;
      });

      // The rune field is the frontmost depth layer, so it travels furthest.
      // The transform lands on the container while the glyphs inside keep
      // their own CSS drift, which is why the two never fight each other.
      if (runeLayer) {
        runeLayer.style.transform =
          "translate(" + -mxLerp * PARALLAX.rune + "px," + -myLerp * PARALLAX.rune + "px)";
      }

      var folParX = -mxLerp * PARALLAX.foliage;
      var folParY = -myLerp * PARALLAX.foliage;
      var rect = root.getBoundingClientRect();

      foliage.forEach(function (f, i) {
        var st = foliageState[i];
        var tx = 0;
        var ty = 0;
        var trot = 0;

        if (pointerActive) {
          var dx = f.ax * rect.width - pointerX;
          var dy = f.ay * rect.height - pointerY;
          var dist = Math.hypot(dx, dy);
          if (dist < FOLIAGE.radius && dist > 0.0001) {
            var fall = 1 - dist / FOLIAGE.radius;
            fall = fall * fall; // quadratic falloff
            tx = (dx / dist) * FOLIAGE.push * fall;
            ty = (dy / dist) * FOLIAGE.push * fall;
            trot = (dx / dist) * FOLIAGE.rot * fall;
          }
        }

        st.x = lerp(st.x, tx, FOLIAGE.lerp);
        st.y = lerp(st.y, ty, FOLIAGE.lerp);
        st.rot = lerp(st.rot, trot, FOLIAGE.lerp);

        f.el.style.transform =
          "translate(" +
          (folParX + st.x).toFixed(2) +
          "px," +
          (folParY + st.y).toFixed(2) +
          "px) rotate(" +
          st.rot.toFixed(2) +
          "deg)";
      });

      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (rafId === null) rafId = requestAnimationFrame(tick);
    }
    function stop() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    // Don't burn frames while the hero is scrolled off screen.
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) start();
          else stop();
        },
        { threshold: 0 }
      );
      io.observe(root);
    }

    /* ---------- boot ---------- */
    // Wait for every layer to decode so the opaque base is painted before the
    // half-transparent portals — otherwise the seam flashes black on load.
    // The foliage layers ship without a src (see hero-portal.liquid) so they
    // don't compete with the base scene for bandwidth on first paint. Promote
    // them once the base has decoded; they have the full intro to arrive
    // before they're faded in at 2200ms.
    function loadFoliage() {
      foliage.forEach(function (f) {
        var src = f.el.getAttribute("data-src");
        if (src && !f.el.getAttribute("src")) f.el.setAttribute("src", src);
      });
    }

    function whenImagesReady(done) {
      // `img[src]` matters: a src-less foliage image is `complete` with
      // naturalWidth 0, so it would never fire load/error and would hold the
      // whole stack hidden until the safety timeout.
      var imgs = Array.prototype.slice.call(root.querySelectorAll(".hero-portal__layers img[src]"));
      var remaining = imgs.length;
      var finished = false;

      function finish() {
        if (finished) return;
        finished = true;
        done();
      }
      function step() {
        if (--remaining <= 0) finish();
      }

      if (remaining === 0) return finish();

      imgs.forEach(function (img) {
        if (img.complete && img.naturalWidth > 0) {
          step();
        } else {
          img.addEventListener("load", step, { once: true });
          img.addEventListener("error", step, { once: true });
        }
      });

      // Never hang if an image stalls or 404s.
      timers.push(setTimeout(finish, 4000));
    }

    attachRunes();

    start();
    whenImagesReady(function () {
      root.classList.remove("is-loading");
      loadFoliage();
      if (reduceMotion) showStaticState();
      else runIntro();
    });

    // Theme editor tears sections down and rebuilds them.
    root.hpDestroy = function () {
      stop();
      timers.forEach(clearTimeout);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("blur", onPointerLeave);
      if (window.QSRuneField && runeLayer) window.QSRuneField.detach(runeLayer);
      delete root.dataset.hpInit;
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(".hero-portal").forEach(initHero);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var el = e.target.querySelector(".hero-portal");
    if (el && el.hpDestroy) el.hpDestroy();
  });
})();
